// useAskStream — the Ask BigAgri SSE client, shared by the ⌘K Ask view and
// the /ask floor mode (spec P1). Owns the fetch + frame parse; telemetry
// (ask_answered / ask_error) fires exactly once per run. Deep links are
// validated against LINK_ALLOWLIST before they ever render.

import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { logHelpEvent } from '@/lib/helpEvents'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string
const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY as string

// Deep links must be relative app routes for the surfaces the tools cover.
// The farm entries are the six FARM tools' links (mig 20261032700000), each a
// real <Route path> in App.tsx — /farm/spray-records and /farm/spray-tasks are
// the redirects into the Sprays hub's Records and Plan tabs (no query strings
// here: the pattern refuses them). askAccess.test.ts reads every link the
// ask_* migrations emit and fails if one would be filtered out here.
const LINK_ALLOWLIST = /^\/(pallets|runs|coldroom|dispatch|deliveries|tipping|labour|stock|bin-inventory|qc|compliance|farm\/(?:spray-records|spray-tasks|harvest-picks|scouting|fert-applications|receipts))(\/[A-Za-z0-9%._-]+)*\/?$/

/** May this server-emitted link render as a "See the data" button? */
export function isAskLink(link: unknown): link is string {
  return typeof link === 'string' && LINK_ALLOWLIST.test(link)
}

export const TOOL_LABEL: Record<string, string> = {
  ask_pallet_lookup:   'Pallet lookup',
  ask_stock_on_hand:   'Stock on hand',
  ask_runs_today:      "Today's runs",
  ask_coldroom_aging:  'Coldroom aging',
  ask_dispatches:      'Dispatches',
  ask_deliveries:      'Deliveries',
  ask_tipping:         'Tipping',
  ask_clockings:       'Attendance',
  ask_packout:         'Packout',
  ask_packaging_stock: 'Packaging stock',
  ask_bins_on_hand:    'Bins on hand',
  ask_qc:              'QC checks',
  ask_compliance_readiness: 'Audit readiness',
  ask_compliance_expiring:  'Expiring items',
  ask_compliance_ncs:       'Non-conformances',
  ask_standard_clauses:     'Standards search',
  // FARM pack (mig 20261032700000)
  ask_spray_status:         'Sprays',
  ask_spray_tasks_due:      'Spray tasks',
  ask_harvest_totals:       'Harvest picks',
  ask_scouting_pressure:    'Scouting',
  ask_fert_applications:    'Fertilising',
  ask_farm_receipts:        'Farm receipts',
  ask_help:                 'Help search',
}

// Prewarm the gateway when an ask surface opens (telemetry 2026-08-04: every
// stall error was the FIRST ask after an idle gap — cold isolate + caches).
// {warm:true} runs the server's gate chain and prefetches the tenant vocab
// cache; no model call, no metering. Throttled so re-opening surfaces doesn't
// spam the gate RPCs; entirely best-effort.
let lastWarmAt = 0
export function prewarmAsk(): void {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return
  if (Date.now() - lastWarmAt < 60_000) return
  lastWarmAt = Date.now()
  void (async () => {
    try {
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token
      if (!token) return
      await fetch(`${SUPABASE_URL}/functions/v1/ask-bigagri`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, apikey: SUPABASE_ANON, 'Content-Type': 'application/json' },
        body: JSON.stringify({ warm: true }),
      })
    } catch { /* best-effort — the real ask carries its own error handling */ }
  })()
}

const ASK_ERROR_COPY: Record<number, string> = {
  401: 'Your session has expired — sign in again and retry.',
  403: 'The assistant is not enabled for this tenant.',
  429: "This month's fair-use limit for the assistant has been reached.",
  501: 'The assistant is not configured yet.',
}

export interface AskState {
  phase:  'streaming' | 'done' | 'error'
  text:   string
  tools:  string[]
  links:  string[]
  error?: string
}

// Stream one question through the gateway. `onDelta` lets a voice surface
// speak sentences as they arrive without re-deriving them from state diffs.
// The returned abort() kills the live stream (barge-in: a PTT press mid-answer
// must stop late sentences, not just the audio) — deliberate, so no telemetry
// and no error state; whatever streamed stays on screen.
// fallow-ignore-next-line complexity
export function useAskStream(
  question: string,
  route: string,
  attempt: number,
  onDelta?: (text: string) => void,
  onDone?: (answer: string) => void,
  history?: Array<{ role: string; content: string }>,
): AskState & { abort: () => void } {
  const [state, setState] = useState<AskState>({ phase: 'streaming', text: '', tools: [], links: [] })
  // Rebound by each run's effect; a no-op before the first run starts.
  const abortRef = useRef<() => void>(() => {})
  const abort = useCallback(() => abortRef.current(), [])

  useEffect(() => {
    let alive = true
    const ctrl = new AbortController()
    const startedAt = Date.now()
    // Terminal-state latch: done / error / abort each log exactly one event.
    // Without it, an ask killed mid-stream (barge-in, leaving the page) left
    // NO terminal telemetry row — read-backs couldn't tell abandonment from
    // success (the orphaned 2026-08-02 ask).
    let terminal = false
    let streamedChars = 0
    const logAbortIfLive = () => {
      if (terminal) return
      terminal = true
      const ms = Date.now() - startedAt
      // A sub-250ms empty abort carries no signal — and StrictMode's dev
      // double-invoke tears the first effect run down instantly; without this
      // guard every dev ask would log a phantom abort.
      if (ms < 250 && streamedChars === 0) return
      logHelpEvent({ eventType: 'ask_abort', route, queryText: `${ms}ms/${streamedChars}ch` })
    }
    setState({ phase: 'streaming', text: '', tools: [], links: [] })

    // Stall watchdog: a wedged connection otherwise streams "Looking that
    // up…" forever. No SSE frame for 25s = abort + an honest error.
    const STALL_MS = 25_000
    let stallTimer: ReturnType<typeof setTimeout> | undefined

    const fail = (msg: string, detail?: string) => {
      clearTimeout(stallTimer)
      if (!alive) return
      terminal = true
      // detail = stage/status ("http_502", "stream_err", "net") — the day-1
      // read-back couldn't distinguish cold-start from provider errors.
      logHelpEvent({ eventType: 'ask_error', route, queryText: detail })
      setState(s => ({ ...s, phase: 'error', error: msg }))
    }

    const armStall = () => {
      clearTimeout(stallTimer)
      stallTimer = setTimeout(() => {
        fail('The assistant timed out — tap retry to ask again.', 'stall')
        ctrl.abort()
      }, STALL_MS)
    }

    // Deliberate barge-in abort: not an error — freeze what streamed as done
    // so the surface stops pulsing. Logged as ask_abort (duration + streamed
    // chars, no question text) so read-backs can see abandonment.
    abortRef.current = () => {
      alive = false
      clearTimeout(stallTimer)
      ctrl.abort()
      logAbortIfLive()
      setState(s => (s.phase === 'streaming' ? { ...s, phase: 'done' } : s))
    }

    // SSE fetch + frame-parse glue — same coverage-driven CRAP class as the
    // gateway's stream parser; splitting it buys indirection, not readability.
    // fallow-ignore-next-line complexity
    ;(async () => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        fail('You are offline — the assistant needs a connection.', 'offline'); return
      }
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token
      if (!token) { fail(ASK_ERROR_COPY[401], 'no_token'); return }

      let res: Response
      armStall()
      try {
        res = await fetch(`${SUPABASE_URL}/functions/v1/ask-bigagri`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, apikey: SUPABASE_ANON, 'Content-Type': 'application/json' },
          body: JSON.stringify({ question, history: history ?? [] }),
          signal: ctrl.signal,
        })
      } catch {
        if (!ctrl.signal.aborted) fail('Could not reach the assistant — check your connection and retry.', 'net')
        return
      }
      if (!res.ok) { fail(ASK_ERROR_COPY[res.status] ?? 'The assistant hit a problem — try again.', `http_${res.status}`); return }

      const reader = res.body!.pipeThrough(new TextDecoderStream()).getReader()
      let buf = ''
      let answer = ''
      for (;;) {
        const { done, value } = await reader.read().catch(() => ({ done: true, value: undefined }))
        if (done || !alive) break
        armStall()
        buf += value
        let idx
        while ((idx = buf.indexOf('\n\n')) >= 0) {
          const frame = buf.slice(0, idx); buf = buf.slice(idx + 2)
          const evLine = frame.split('\n').find(l => l.startsWith('event: '))
          const dataLine = frame.split('\n').find(l => l.startsWith('data: '))
          if (!evLine || !dataLine) continue
          const event = evLine.slice(7).trim()
          let data: Record<string, unknown> = {}
          try { data = JSON.parse(dataLine.slice(6)) } catch { continue }
          if (event === 'delta') {
            const text = String(data.text ?? '')
            answer += text
            streamedChars += text.length
            onDelta?.(text)
            setState(s => ({ ...s, text: s.text + text }))
          } else if (event === 'tool') {
            const name = String(data.name ?? '')
            setState(s => (s.tools.includes(name) ? s : { ...s, tools: [...s.tools, name] }))
          } else if (event === 'done') {
            clearTimeout(stallTimer)
            terminal = true
            const links = (Array.isArray(data.links) ? data.links : [])
              .filter(isAskLink)
            const ms = Date.now() - startedAt
            // cr/cw only when the gateway sent them (older gateways omit
            // them); the leading "<ms>ms" prefix is load-bearing — the
            // latency reports regex on '^(\d+)ms'.
            const cr = data.cache_read, cw = data.cache_write
            const cache = typeof cr === 'number' && typeof cw === 'number' ? `/cr${cr}/cw${cw}` : ''
            logHelpEvent({ eventType: 'ask_answered', route, queryText: `${ms}ms/${data.rounds ?? '?'}r${cache}` })
            // The answer text is the review corpus — "answers aren't great"
            // is only fixable if we can read what it actually said.
            logHelpEvent({ eventType: 'ask_answer', route, queryText: answer })
            setState(s => ({ ...s, phase: 'done', links }))
            onDone?.(answer)
            return
          } else if (event === 'err') {
            fail('The assistant hit a problem — try again.', 'stream_err'); return
          }
        }
      }
      clearTimeout(stallTimer)
      // Reaching here alive means the stream ended without done/err — a real
      // failure that previously left no telemetry row at all.
      if (alive) fail('The connection dropped mid-answer — retry.', 'dropped')
    })()

    // Leaving the surface (or re-asking) mid-stream is an abandonment too.
    return () => { alive = false; clearTimeout(stallTimer); ctrl.abort(); logAbortIfLive() }
    // onDelta/onDone/history are intentionally not deps — surfaces pass
    // stable or ask-time-snapshotted values and a re-run would abort the
    // live stream.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, route, attempt])

  return { ...state, abort }
}
